import { FormEvent, useCallback, useEffect, useState } from "react";
import { kpsiGet, kpsiPost, type KpsiCompanyOption } from "./kpsiApi";

type Row = {
  id: number;
  position: string;
  fullName: string;
  score: string;
  stage: number;
  stageName: string;
  dateFaxDisplay: string;
  downloadDisplay: string;
  color: string;
};

type ListResult = {
  rows: Row[];
  page: number;
  pageCount: number;
  total: number;
  size: number;
  sort: string;
  dir: string;
  query?: string | null;
};

interface Props {
  companyId: number;
  companies: KpsiCompanyOption[];
  isAdmin: boolean;
  onCompanyChange: (id: number) => void;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onAdd: () => void;
  onFlash: (msg: string) => void;
}

export default function KpsiTestsList(props: Props) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("fax");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [data, setData] = useState<ListResult | null>(null);
  const [undoId, setUndoId] = useState<number | null>(null);

  const load = useCallback(async () => {
    const qs = new URLSearchParams({
      companyId: String(props.companyId),
      sort,
      dir,
      page: String(page),
      size: String(size),
    });
    if (q.trim()) qs.set("q", q.trim());
    const result = await kpsiGet<ListResult>(`/Kpsi/Tests?${qs}`);
    setData(result);
  }, [props.companyId, q, sort, dir, page, size]);

  useEffect(() => {
    void load().catch((e) => props.onFlash(e instanceof Error ? e.message : "Load failed"));
  }, [load, props]);

  function onSearch(e: FormEvent) {
    e.preventDefault();
    setPage(1);
    void load();
  }

  function toggleSort(column: string) {
    if (sort === column) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(column);
      setDir(column === "fax" || column === "download" || column === "stage" ? "desc" : "asc");
    }
    setPage(1);
  }

  function sortMark(column: string) {
    if (sort !== column) return "";
    return dir === "asc" ? " ▲" : " ▼";
  }

  async function onDelete(id: number) {
    await kpsiPost(`/Kpsi/Tests/${id}/Delete`);
    setUndoId(id);
    props.onFlash("Test has been deleted.");
    await load();
  }

  async function onUndelete() {
    if (undoId == null) return;
    await kpsiPost(`/Kpsi/Tests/${undoId}/Undelete`);
    setUndoId(null);
    props.onFlash("Test has been brought back.");
    await load();
  }

  return (
    <>
      <h1>View Records</h1>
      {undoId != null && (
        <div className="kpsi-alert">
          <span>Test deleted.</span>
          <button type="button" onClick={() => void onUndelete()}>Undo</button>
        </div>
      )}
      <form className="kpsi-toolbar" onSubmit={onSearch}>
        <div>
          <label htmlFor="kpsi-company">Company</label>
          <select
            id="kpsi-company"
            value={props.companyId}
            onChange={(e) => {
              props.onCompanyChange(Number(e.target.value));
              setPage(1);
            }}
          >
            {props.companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="kpsi-q">Search</label>
          <input
            id="kpsi-q"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, position, or score"
          />
        </div>
        <div>
          <label htmlFor="kpsi-size">Show</label>
          <select id="kpsi-size" value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}>
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n} entries</option>
            ))}
          </select>
        </div>
        <div>
          <button type="submit">Search</button>
          {q && (
            <button type="button" onClick={() => { setQ(""); setPage(1); }}>Clear</button>
          )}
        </div>
        <div className="ms-auto">
          <button type="button" onClick={props.onAdd}>Add New Record</button>
        </div>
      </form>

      <table className="kpsi-records">
        <thead>
          <tr>
            <th><button type="button" onClick={() => toggleSort("position")}>Position Sought{sortMark("position")}</button></th>
            <th><button type="button" onClick={() => toggleSort("name")}>User{sortMark("name")}</button></th>
            <th><button type="button" onClick={() => toggleSort("score")}>Score{sortMark("score")}</button></th>
            <th><button type="button" onClick={() => toggleSort("fax")}>Fax Received{sortMark("fax")}</button></th>
            <th><button type="button" onClick={() => toggleSort("download")}>Download Date{sortMark("download")}</button></th>
            <th><button type="button" onClick={() => toggleSort("stage")}>Process Stage{sortMark("stage")}</button></th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {!data?.rows?.length && (
            <tr><td colSpan={7} className="kpsi-muted">No matching records found</td></tr>
          )}
          {data?.rows?.map((r) => (
            <tr key={r.id} className={`stage${r.stage}`}>
              <td>{r.position}</td>
              <td className={r.color}>{r.fullName}</td>
              <td>{r.score}</td>
              <td>{r.dateFaxDisplay}</td>
              <td>{r.downloadDisplay}</td>
              <td>{r.stageName}</td>
              <td className="actions">
                <button type="button" onClick={() => props.onView(r.id)}>View</button>
                {" | "}
                <button type="button" onClick={() => props.onEdit(r.id)}>Edit</button>
                {props.isAdmin && (
                  <>
                    {" | "}
                    <button type="button" onClick={() => void onDelete(r.id)}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {data && data.pageCount > 1 && (
        <div className="kpsi-pager">
          <button type="button" disabled={page <= 1} onClick={() => setPage(1)}>First</button>
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {data.page} of {data.pageCount} ({data.total})</span>
          <button type="button" disabled={page >= data.pageCount} onClick={() => setPage((p) => p + 1)}>Next</button>
          <button type="button" disabled={page >= data.pageCount} onClick={() => setPage(data.pageCount)}>Last</button>
        </div>
      )}
    </>
  );
}
